// @flow

import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import system from "system-components";

import theme from "../../../../themes/policystat/theme";
import { StyledFontAwesomeIcon } from "../../../partials/icons";

import tocStyles from "./tocstyles";

import { H3, Text } from "pstat-design-system/text";
import { STYLED_FLOATING_BUTTON_HEIGHT } from "pstat-design-system/shared/Button";
import RouterLink from "pstat-design-system/RouterLink";
import type { HeadingNode } from "pstat-anywhere/utils/domHelpers";
import { TERTIARY_NAVBAR_HEIGHT } from "pstat-anywhere/components/partials/tertiary_nav/TertiaryNav";
import { FLEX_HEADER_HEIGHT } from "pstat-anywhere/components/partials/HamburgerHeader";

const TocOl = styled("ol")`
  list-style: none;
`;

TocOl.displayName = "TocOl";

export const TocItem = ({
  item,
  onClick,
  activeItem,
  level
}: {
  item: HeadingNode
}) => {
  let active = null;
  if (activeItem) {
    active = activeItem.id === item.id ? true : false;
  }
  const getOffset = level => {
    let total = 0;
    for (let i = 0; i <= level; i++) {
      total += theme.space[i];
    }
    total += theme.space[4];
    return total;
  };
  const offset = getOffset(level);
  return (
    <Fragment>
      <Text
        is="li"
        className={item.className}
        bg={active ? "secondary.0" : ""}
        id={`toc-${item.id}`}
        fontWeight={item.level % 2 === 0 ? "medium" : "normal"}
        size={item.level % 2 === 0 ? "small" : "normal"}
        pl={offset}
        mb={1}
      >
        <RouterLink
          to={{
            pathname: window.location.pathname,
            hash: `#${item.id}`
          }}
          color={active ? "white" : "secondary.0"}
        >
          <span dangerouslySetInnerHTML={{ __html: item.content }} />
        </RouterLink>
      </Text>

      {item.children && (
        <TocLevel
          sublevels={item.children}
          activeItem={activeItem}
          level={level + 1}
        />
      )}
    </Fragment>
  );
};

export const TocAttachments = ({ count }) => {
  const { t } = useTranslation();
  return (
    <Fragment>
      <Text
        is="ul"
        color="secondary.0"
        fontWeight="normal"
        size="normal"
        my={3}
        pl={4}
      >
        <StyledFontAwesomeIcon
          icon={["fal", "paperclip"]}
          size="md"
          color="secondary.0"
          mx="0"
        />
        <RouterLink
          to={{
            pathname: window.location.pathname,
            hash: `#attachments`
          }}
        >
          {t("documentControl.view.tableOfContents.attachments.header", {
            count
          })}
        </RouterLink>
      </Text>
      <AttachmentsDivider />
    </Fragment>
  );
};

export const TocLevel = ({
  sublevels,
  activeItem,
  level
}: {
  sublevels: Array<HeadingNode>
}) => {
  if (sublevels.length > 0) {
    return (
      <TocOl>
        {sublevels.map(child => (
          <TocItem
            activeItem={activeItem}
            item={child}
            key={child.id}
            level={level}
          />
        ))}
      </TocOl>
    );
  }
  return null;
};

const AttachmentsDivider = system({
  mb: 2,
  mr: 5,
  borderBottom: 2,
  borderColor: "nav.90"
});

const TocContainer = system({
  bg: "nav.100",
  pb: 4,
  width: ["100vw", "100vw", "320px"],
  minHeight: ["100px", "100px", "400px"],
  boxShadow: `0px 2px 32px 0 rgba(0, 0, 0, 0.16), 0px 1px 6px 0 rgba(0, 0, 0, 0.06)`,
  borderRadius: `0 ${theme.radii[1]}px ${theme.radii[1]}px 0`,
  mb: 2
}).extend`
  max-height: ${props => props.maxHeight}px;
  overflow-y: auto;
  z-index: ${theme.layers.secondaryNav - 1};
  ${tocStyles};
  & ol {
    margin-top: ${theme.space[2]}px;
  & ol li {
    margin-bottom: ${theme.space[1]}px
  }
`;

TocContainer.displayName = "TocContainer";

const TocItemsContainer = system({
  is: "section",
  pl: 0
});

export const TableOfContents = ({
  rootNode,
  countAttachments,
  activeItem
}: {
  rootNode: HeadingNode
}) => {
  const { t } = useTranslation();
  const [totalHeight, setTotalHeight] = useState(0);
  useEffect(() => {
    const primaryHeight = FLEX_HEADER_HEIGHT;
    const secondaryNav = document.getElementById("secondary-nav");
    let secondaryHeight = 0;
    if (secondaryNav != null) {
      secondaryHeight = secondaryNav.getBoundingClientRect().height;
    }

    const tertiaryHeight = TERTIARY_NAVBAR_HEIGHT;

    const TOCFloatingButtonHeight = STYLED_FLOATING_BUTTON_HEIGHT;
    const paddingHeight = 24 + 24 + 24; //button bot, table bot, table top

    setTotalHeight(
      window.innerHeight -
        (primaryHeight +
          secondaryHeight +
          tertiaryHeight +
          TOCFloatingButtonHeight +
          paddingHeight)
    );
  }, []);
  return (
    <TocContainer data-testId="toc-expanded-component" maxHeight={totalHeight}>
      <H3 bg="nav.10" size="lg" color="nav.100" height={50} px={2} py={3}>
        <StyledFontAwesomeIcon
          icon={["fas", "list-alt"]}
          color="nav.100"
          mr={2}
        />
        {t("documentControl.view.tableOfContents.tab.TOC.header")}
      </H3>
      <TocItemsContainer>
        {countAttachments > 0 && <TocAttachments count={countAttachments} />}
        <TocLevel
          sublevels={rootNode.children}
          activeItem={activeItem}
          level={0}
        />
      </TocItemsContainer>
    </TocContainer>
  );
};

export default TableOfContents;
